import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { BellIcon, BellOffIcon, CalendarPlusIcon, CheckIcon, ClockIcon, MailIcon, MessageCircleIcon, PhoneIcon, UserIcon, VideoIcon, XIcon } from 'lucide-react';
import { Badge } from '../ui/Badge';
import { DeleteButton } from '../ui/DeleteButton';
import { ProductImage } from '../../ui/ProductImage';
import { useAdmin } from '../AdminContext';
import { whatsappNumber } from '../customers/customerRules';
import { btnGhost, btnPrimary, btnSecondary } from '../ui/styles';
import { TimeChoice } from './TimeChoice';
import { CompleteForm } from './CompleteForm';
import { PerformerRow } from './PerformerRow';
import { useCan } from '../useCan';
import { firstName, stateOf } from './agendaRules';
import { REMINDER_HOURS, endTime, hasStarted, isActive, reminderState } from '../../core/rules/agenda';
import { useNow } from '../../hooks/useNow';
import { telHref } from '../../core/rules/contact';
import { byStart, dayLong, dayMedium, today } from '../../core/format/dates';
import { FollowUpForm } from './FollowUpForm';
import { staffList } from '../../core/rules/salonDays';
import { bookingProblem, courseWarning, requestProblem } from '../../core/rules/staffPermissions';
import { ADMIN_BASE } from '../../utils/hosts';
import type { AgendaAppointment, AgendaBlock } from '../../core/types/agenda';
import type { AgendaControl } from './useAgenda';
type Props = {
  appointment: AgendaAppointment;
  /** Alle afspraken van de geladen week, voor vrije tijden en "vorige keer". */
  appointments: AgendaAppointment[];
  blocks: AgendaBlock[];
  agenda: AgendaControl;
  onClose: () => void;
};
type Mode = 'view' | 'move' | 'complete' | 'followup';

/** Zelfde klant: op id, of (bij gasten) op een ingevuld e-mailadres. Twee lege adressen zijn niet dezelfde. */
const sameCustomer = (x: AgendaAppointment['customer'], y: AgendaAppointment['customer']) => x.id || y.id ? x.id === y.id : Boolean(x.email) && x.email.toLowerCase() === y.email.toLowerCase();
const reminderText = {
  verstuurd: 'Herinnering verstuurd',
  gepland: `Herinnering gaat ${REMINDER_HOURS} uur vooraf`,
  'te-kort': 'Geen herinnering: binnen een dag geboekt',
  geen: ''
} as const;

/** Eén afspraak: wie, wat, wanneer, en wat Sarah ermee kan. De acties volgen de dag. */
export function AppointmentDetail({
  appointment: a,
  appointments,
  blocks,
  agenda,
  onClose
}: Props) {
  const {
    index,
    site
  } = useAdmin();
  const can = useCan();
  // Plannen voor de hele salon (Sarah) of alleen op eigen dagen (collega): bookingProblem/requestProblem.
  const viewer = can.viewer('agenda.plan');
  const navigate = useNavigate();
  const team = staffList(site.staff);
  const rules = {
    hours: site.openingHours,
    exceptions: site.salonExceptions,
    staff: team
  };
  const [mode, setMode] = useState<Mode>('view');
  const [date, setDate] = useState(a.date);
  const [time, setTime] = useState(a.time);
  const [overlap, setOverlap] = useState(false);
  const [confirmNoShow, setConfirmNoShow] = useState(false);
  const busy = agenda.isBusy(a.id);
  const treatment = index.treatmentBySlug(a.treatmentSlug);
  const state = stateOf(a);
  const open = isActive(a);
  // Elke minuut opnieuw: "Afronden" verschijnt op de begintijd, niet pas na een andere klik.
  const now = useNow();
  const started = hasStarted(a, now);
  const reminder = open ? reminderState(a, now) : 'geen';
  const wa = a.customer.phone ? whatsappNumber(a.customer.phone) : '';

  // De vorige afgeronde behandeling van deze klant in de geladen week(en): wat ze toen gebruikte.
  const previous = appointments.filter(x => x.id !== a.id && x.outcome === 'afgerond' && sameCustomer(x.customer, a.customer) && byStart(x, a) < 0).sort((x, y) => byStart(y, x))[0];
  const name = (id: string) => index.productById(id)?.name ?? id;
  // Collega: aanvragen alleen op haar eigen dag; verzetten alleen naar een eigen dag.
  const requestBlocked = a.status === 'aanvraag' ? requestProblem(viewer, a, rules) : null;
  const moveProblem = mode === 'move' && date ? bookingProblem(viewer, {
    date,
    treatmentSlug: a.treatmentSlug
  }, rules) : null;
  // Kuursessie bij een ander dan bij wie de kuur loopt: alleen een waarschuwing.
  const kuurWarning = a.session && open ? courseWarning(a.customer.kuur?.staffId, a.staffId, team) : '';

  // Escape sluit normaal het paneel (Agenda.tsx). Tijdens afronden niet: dan gaat het ingevulde verloren.
  useEffect(() => {
    if (mode !== 'complete') return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && e.preventDefault();
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [mode]);
  return <div className="space-y-5" data-id="element-253" data-mp="37q7mrumnygl9">
      <div className="flex items-start justify-between gap-3" data-id="element-254" data-mp="37raa5sks20wf">
        <div className="min-w-0" data-id="element-255" data-mp="3lpgzgkbnuskh">
          <div className="flex flex-wrap items-center gap-1.5" data-id="element-256" data-mp="3lqicuvg5fr5d">
            <Badge tone={state.tone} data-id="element-257" data-mp="3gqxdijx2hgs4">{state.label}</Badge>
            {a.customer.firstVisit && open && <Badge tone="soft" data-id="element-258" data-mp="47b3s2c5nmw3">Eerste bezoek</Badge>}
            {a.session && <Badge tone="neutral" data-id="element-259" data-mp="47upzv8u8xfx">Kuursessie {a.session}</Badge>}
          </div>
          <h2 className="mt-2 font-display text-[1.5rem] font-light leading-tight text-ink" data-id="element-260" data-mp="94tcpsq6a243">{a.customer.name}</h2>
          <p className="mt-0.5 text-[14px] text-ink" data-id="element-261" data-mp="3rbhkxrgir5dz">{treatment?.name ?? a.treatmentSlug}</p>
        </div>
        {/* Tijdens afronden geen kruisje: één misklik zou ingevulde producten en advies weggooien. "Terug" staat in het formulier. */}
        {mode !== 'complete' && <button type="button" onClick={onClose} className={btnGhost} aria-label="Sluiten" data-id="element-262" data-mp="3mdg297n09831">
            <XIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-263" data-mp="37otdygfeh5zo" />
          </button>}
      </div>

      <dl className="space-y-2 text-[14px]" data-id="element-264" data-mp="3cne9i2jpj1hs">
        <div className="flex items-center gap-2.5" data-id="element-265" data-mp="2ynbzf2hn5cd3">
          <dt className="sr-only" data-id="element-266" data-mp="2ynlmah7e33ek">Wanneer</dt>
          <ClockIcon className="h-4 w-4 shrink-0 text-muted" strokeWidth={1.75} aria-hidden="true" data-id="element-267" data-mp="33lwy2xh8vnzf" />
          <dd className="text-ink" data-id="element-268" data-mp="195ok1yjdx2w5">
            <span className="capitalize" data-id="element-269" data-mp="196q4gtmaj1qj">{dayLong(a.date)}</span>, {a.time}–{endTime(a)}
            <span className="text-muted" data-id="element-270" data-mp="14e6qvwnls6lq"> · {a.durationMinutes} min</span>
          </dd>
        </div>
        {a.kind === 'video' && <div className="flex items-center gap-2.5" data-id="element-271" data-mp="ze29m4evf7ug">
            <dt className="sr-only" data-id="element-272" data-mp="vfcplszbus1u">Soort</dt>
            <VideoIcon className="h-4 w-4 shrink-0 text-muted" strokeWidth={1.75} aria-hidden="true" data-id="element-273" data-mp="vcgqw2fnnzcc" />
            <dd className="text-ink" data-id="element-274" data-mp="1s2pxm7r08bbp">Videoconsult</dd>
          </div>}
        <PerformerRow appointment={a} agenda={agenda} canChange={can.canAll('agenda.plan') && open} data-id="element-275" data-mp="1n690io1tummh" />
        {kuurWarning && <p className="rounded-md bg-sand px-3 py-2 text-[13px] text-ink" data-id="element-276" data-mp="1j68mv5mjyfgh">{kuurWarning}</p>}
        {a.handledBy && team.length > 1 && <p className="text-[13px] text-muted" data-id="element-277" data-mp="7pid5cix75i4">
            {a.handledBy.action === 'bevestigd' ? 'Bevestigd' : 'Andere tijd voorgesteld'} door {a.handledBy.name}
          </p>}
        {a.arrivedAt && open && <p className="text-[13px] text-muted" data-id="element-278" data-mp="2ee0v6ns7xeky">Binnen sinds {a.arrivedAt.slice(11, 16)}</p>}
        {reminder !== 'geen' && <div className="flex items-center gap-2.5" data-id="element-279" data-mp="2jcu6c9k18thy">
            <dt className="sr-only" data-id="element-280" data-mp="2nde51hk98567">Herinnering</dt>
            {reminder === 'te-kort' ? <BellOffIcon className="h-4 w-4 shrink-0 text-muted" strokeWidth={1.75} aria-hidden="true" data-id="element-281" data-mp="2s4e34q540taa" /> : <BellIcon className="h-4 w-4 shrink-0 text-muted" strokeWidth={1.75} aria-hidden="true" data-id="element-282" data-mp="2x2nxiyj0fpuu" />}
            <dd className="text-muted" data-id="element-283" data-mp="3218y4z26cbjt">{reminderText[reminder]}</dd>
          </div>}
      </dl>

      {/* Contact: bellen als ze te laat is, appen om te verzetten. */}
      <div className="flex flex-wrap gap-1.5" data-id="element-284" data-mp="3b3s07gy4yk50">
        {a.customer.phone && <a href={telHref(a.customer.phone)} className={btnSecondary} data-id="element-285" data-mp="1co5k6yj0qwox">
            <PhoneIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-286" data-mp="1cq8gg0arlm23" />
            Bellen
          </a>}
        {wa && <a href={`https://wa.me/${wa}`} target="_blank" rel="noreferrer" className={btnSecondary} data-id="element-287" data-mp="xsfdx2c62886">
            <MessageCircleIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-288" data-mp="suxbzvwkgt9o" />
            WhatsApp
          </a>}
        {a.customer.email && <a href={`mailto:${a.customer.email}`} className={btnSecondary} data-id="element-289" data-mp="12jxe3o6d84s3">
            <MailIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-290" data-mp="12m1sj01w4m6m" />
            Mail
          </a>}
        {a.customer.id && <Link to={`${ADMIN_BASE}/klanten/${a.customer.id}`} className={btnSecondary} data-id="element-291" data-mp="9w1of3f7rd4t">
            <UserIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-292" data-mp="nvkjovmxetzp" />
            Dossier
          </Link>}
      </div>

      {/* Afrekenen in de kassa: na afronden (of al tijdens de behandeling), zolang er geen bon is. */}
      {a.kind === 'salon' && a.status === 'bevestigd' && a.outcome !== 'geannuleerd' && a.outcome !== 'no-show' && (a.outcome === 'afgerond' || started) && <div className="flex flex-wrap items-center gap-2 border-t border-line pt-4 text-[13px]" data-id="element-293" data-mp="22rvufx2lz30t">
          {a.saleId ? <span className="text-muted" data-id="element-294" data-mp="26uidp9l7rfme">Afgerekend in de kassa.</span> : <Link to={`${ADMIN_BASE}/kassa?afspraak=${a.id}`} className={a.outcome === 'afgerond' ? btnPrimary : btnSecondary} data-id="element-295" data-mp="1suzhnf5ofuqg">
              Afrekenen
            </Link>}
          {a.outcome === 'afgerond' && mode === 'view' && <button type="button" onClick={() => setMode('followup')} className={btnSecondary} data-id="element-296" data-mp="2lkpwxrj1p2o5">
              <CalendarPlusIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-297" data-mp="2qjsr2gxhlohc" />
              Plan vervolg
            </button>}
        </div>}

      {/* Na afloop: wat er gebruikt en meegegeven is. */}
      {a.outcome === 'afgerond' && (a.products?.used.length || a.products?.advice.length || a.treatmentNote) && <section className="space-y-3 border-t border-line pt-4" data-id="element-298" data-mp="398h9mw8hzhpy">
          {a.products && a.products.used.length > 0 && <p className="text-[13px] text-muted" data-id="element-299" data-mp="28c6b7hedabfz">
              <span className="text-ink" data-id="element-300" data-mp="2dahj3ml3qbvk">Gebruikt:</span> {a.products.used.map(name).join(', ')}
            </p>}
          {a.products && a.products.advice.length > 0 && <div data-id="element-301" data-mp="1zdb7qd2pjfgd">
              <p className="text-[13px] text-ink" data-id="element-302" data-mp="249t8z2ytwvny">Advies voor thuis</p>
              <ul className="mt-2 space-y-1.5" data-id="element-303" data-mp="24bw606yefp26">
                {a.products.advice.map(id => {
            const p = index.productById(id);
            return <li key={id} className="flex items-center gap-2.5 text-[13px]" data-id="element-304" data-mp="1ud6m68mxzuja">
                      {p && <ProductImage src={p.image} className="h-8 w-8 shrink-0 rounded bg-sand object-contain p-0.5" data-id="element-305" data-mp="1gm1tjqhzs2ig" />}
                      <span className="text-ink" data-id="element-306" data-mp="1kjzkoyvboyoh">{name(id)}</span>
                    </li>;
          })}
              </ul>
              <p className="mt-2 text-[12px] text-muted" data-id="element-307" data-mp="1blxapttm2vp2">{a.customer.id ? 'Staat bij haar bezoek in Mijn omgeving, met een link om te bestellen.' : 'Meegestuurd in de mail na de behandeling.'}</p>
            </div>}
          {a.treatmentNote && <p className="whitespace-pre-line rounded-md bg-sand px-3 py-2.5 text-[13px] text-ink" data-id="element-308" data-mp="n2c3vpl762fp">{a.treatmentNote}</p>}
        </section>}

      {open && mode === 'view' && <section className="space-y-3 border-t border-line pt-4" data-id="element-309" data-mp="wxvy87k44t2c">
          {previous?.products && (previous.products.advice.length > 0 || previous.treatmentNote) && <div className="rounded-md bg-sand px-3 py-2.5 text-[13px]" data-id="element-310" data-mp="15zwub13m192f">
              <p className="text-muted" data-id="element-311" data-mp="1ko9r20aiuvxz">Vorige keer ({dayMedium(previous.date)})</p>
              {previous.products.advice.length > 0 && <p className="mt-0.5 text-ink" data-id="element-312" data-mp="1fon4v1cpu5d1">Advies: {previous.products.advice.map(name).join(', ')}</p>}
              {previous.treatmentNote && <p className="mt-0.5 text-ink" data-id="element-313" data-mp="1tphd9jzrk6bp">{previous.treatmentNote}</p>}
            </div>}

          {requestBlocked && <p className="text-[13px] text-muted" data-id="element-314" data-mp="1pl9mzidnqibd">{requestBlocked} Sarah handelt deze aanvraag af.</p>}
          <div className="flex flex-wrap gap-2" data-id="element-315" data-mp="1wog6kagjz4lb">
            {a.status === 'aanvraag' && !requestBlocked && <button type="button" onClick={() => void agenda.confirm(a.id)} disabled={busy} aria-busy={busy} className={btnPrimary} data-id="element-316" data-mp="21nb129nvuhl9">
                <CheckIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-317" data-mp="2bfozasfr562o" />
                Bevestigen
              </button>}
            {a.status === 'bevestigd' && !started && !a.arrivedAt && a.date === today() && <button type="button" onClick={() => void agenda.arrive(a.id)} disabled={busy} className={btnSecondary} data-id="element-318" data-mp="2ki82jdng9kpg">
                Binnen
              </button>}
            {a.status === 'bevestigd' && started && <button type="button" onClick={() => setMode('complete')} disabled={busy} className={btnPrimary} data-id="element-319" data-mp="v1grk3zn51b3">
                <CheckIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-320" data-mp="zzi92khf4skg" />
                Afronden
              </button>}
            {!requestBlocked && <button type="button" onClick={() => setMode('move')} disabled={busy} className={btnSecondary} data-id="element-321" data-mp="38k39rhaqtjnv">
                {a.status === 'aanvraag' ? 'Andere tijd voorstellen' : 'Verzetten'}
              </button>}
            {a.status === 'bevestigd' && a.customer.email && <button type="button" onClick={() => setMode('followup')} disabled={busy} className={btnGhost} data-id="element-322" data-mp="3rp7jnicpoc">
                <CalendarPlusIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-323" data-mp="3s7ar9nz6n566" />
                Plan vervolg
              </button>}
            {started && !confirmNoShow && <button type="button" onClick={() => setConfirmNoShow(true)} disabled={busy} className={btnGhost} data-id="element-324" data-mp="3ia62rhhv6xm7">
                Niet gekomen
              </button>}
          </div>
          {/* Niet gekomen sluit de afspraak af en is niet terug te draaien: één bevestiging, met wat het betekent. */}
          {confirmNoShow && <div className="rounded-md bg-sand px-3 py-3 text-[13px]" role="alertdialog" aria-label="Niet gekomen bevestigen" data-id="element-325" data-mp="3upw0cx0uvui0">
              <p className="text-ink" data-id="element-326" data-mp="3lqhd06fzk01q">
                Afspraak afsluiten als niet gekomen?{' '}
                {a.session ? site.policy.noShowUsesSession ? `Sessie ${a.session} van de kuur telt als gebruikt (no-show-regel staat aan).` : 'De kuursessie telt niet en het nummer komt vrij.' : ''}{' '}
                {firstName(a.customer.name)} krijgt geen bericht.
              </p>
              <div className="mt-2.5 flex flex-wrap gap-2" data-id="element-327" data-mp="8b2uf8b6u03q">
                <button type="button" disabled={busy} aria-busy={busy} onClick={async () => {
            await agenda.noShow(a.id);
            setConfirmNoShow(false);
          }} className={btnSecondary} data-id="element-328" data-mp="da608uyzsacr">
                  {busy ? 'Bezig…' : 'Ja, niet gekomen'}
                </button>
                <button type="button" disabled={busy} onClick={() => setConfirmNoShow(false)} className={btnGhost} data-id="element-329" data-mp="2lh9b27u2tm39">
                  Terug
                </button>
              </div>
            </div>}
          <DeleteButton label="Annuleren" warning={`${firstName(a.customer.name)} krijgt bericht`} onConfirm={() => void agenda.cancel(a.id)} data-id="element-330" data-mp="2ujii2nq4yxbw" />
        </section>}

      {open && mode === 'move' && <section className="space-y-4 border-t border-line pt-4" data-id="element-331" data-mp="22tdygra7aebq">
          <TimeChoice date={date} onDate={setDate} time={time} onTime={setTime} durationMinutes={a.durationMinutes} appointments={appointments} blocks={blocks} ignoreId={a.id} overlap={overlap} onOverlap={setOverlap} data-id="element-332" data-mp="2gjhf92qb9fts" />
          {moveProblem && <p role="alert" className="text-[13px] text-clay" data-id="element-333" data-mp="3j6cup6gya5z7">
              {moveProblem}
            </p>}
          <div className="flex flex-wrap gap-2" data-id="element-334" data-mp="3o4ee5siag7b7">
            <button type="button" disabled={busy || !time || date === a.date && time === a.time || Boolean(moveProblem)} aria-busy={busy} onClick={async () => {
          const moved = await agenda.move(a.id, {
            date,
            time,
            ...(overlap ? {
              allowOverlap: true
            } : {})
          });
          if (moved) setMode('view');
        }} className={btnPrimary} data-id="element-335" data-mp="3o33b5uwo33l5">
              {time ? `Verzet naar ${dayMedium(date)} ${time}` : 'Kies een tijd'}
            </button>
            <button type="button" onClick={() => setMode('view')} className={btnGhost} data-id="element-336" data-mp="3e78ngldl6ibi">
              Laat staan
            </button>
          </div>
          <p className="text-[12px] text-muted" data-id="element-337" data-mp="ph8si2vdmpq8">{firstName(a.customer.name)} krijgt bericht met de nieuwe tijd; de uitnodiging in haar agenda schuift mee.</p>
        </section>}

      {open && mode === 'complete' && <section className="border-t border-line pt-4" data-id="element-338" data-mp="29xgorkb0n2rf">
          <CompleteForm appointment={a} previousUsed={previous?.products?.used ?? []} busy={busy} onCancel={() => setMode('view')} onSubmit={async req => {
        const done = await agenda.complete(a.id, req);
        if (!done) return;
        setMode('view');
        // Afronden → afrekenen: meteen de bon, met de behandeling (uit kuur als het kan) en het advies.
        if (done.kind === 'salon' && !done.saleId) navigate(`${ADMIN_BASE}/kassa?afspraak=${done.id}`);
      }} data-id="element-339" data-mp="29xqd73gex1v0" />
        </section>}

      {mode === 'followup' && <section className="border-t border-line pt-4" data-id="element-340" data-mp="3gldi1s9thfxv">
          <FollowUpForm appointment={a} appointments={appointments} blocks={blocks} agenda={agenda} onCancel={() => setMode('view')} onDone={() => setMode('view')} data-id="element-341" data-mp="3gkvq37u6tnvr" />
        </section>}
    </div>;
}